// Hooks for the platform_settings store + per-feed-source weight batch.
// Phase 1G — admin-only on the BFF.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

/** Raw key/value map; keys follow `<group>.<name>` (e.g. retention.iocs_days). */
export type SettingsMap = Record<string, unknown>;

const KEY = ["admin", "platformSettings"] as const;
const WEIGHTS_KEY = ["admin", "feedSourceWeights"] as const;

export function useSettings() {
    return useQuery({
        queryKey: KEY,
        queryFn: () =>
            api
                .get<{ settings: SettingsMap }>("/api/v2/settings")
                .then((d) => d.settings),
        staleTime: 30_000,
    });
}

export function usePatchSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (updates: SettingsMap) =>
            api.patch<{ settings: SettingsMap }>("/api/v2/settings", { updates }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}

export interface FeedSourceWeight {
    slug: string;
    name: string;
    weight: number;
}

export function useFeedSourceWeights() {
    return useQuery({
        queryKey: WEIGHTS_KEY,
        queryFn: () =>
            api
                .get<{ weights: FeedSourceWeight[] }>("/api/v2/feed-sources/weights")
                .then((d) => d.weights),
        staleTime: 30_000,
    });
}

export function usePatchFeedSourceWeights() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (updates: Record<string, number>) =>
            api.patch<void>("/api/v2/feed-sources/weights", { updates }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: WEIGHTS_KEY });
        },
    });
}

// ---- score config (verdict-builder confidence tunables) -----------------

export type ScoreConfigMap = Record<string, unknown>;

const SCORE_KEY = ["admin", "scoreConfig"] as const;

export function useScoreConfig() {
    return useQuery({
        queryKey: SCORE_KEY,
        queryFn: () =>
            api
                .get<{ scoreConfig: ScoreConfigMap }>("/api/v2/score-config")
                .then((d) => d.scoreConfig),
        staleTime: 30_000,
    });
}

export function usePatchScoreConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (updates: ScoreConfigMap) =>
            api.patch<{ scoreConfig: ScoreConfigMap }>("/api/v2/score-config", { updates }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: SCORE_KEY });
        },
    });
}

// ---- approval gates (per-category approver counts) ---------------------

export interface ApprovalGate {
    category: string;
    approversCount: number;
}

const GATES_KEY = ["admin", "approvalGates"] as const;

export function useApprovalGates() {
    return useQuery({
        queryKey: GATES_KEY,
        queryFn: () =>
            api
                .get<{ gates: ApprovalGate[] }>("/api/v2/approval-gates")
                .then((d) => d.gates),
        staleTime: 30_000,
    });
}

export function usePatchApprovalGates() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (updates: Record<string, number>) =>
            api.patch<void>("/api/v2/approval-gates", { updates }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: GATES_KEY });
        },
    });
}
